/**
 * NoteLine: the one-line explanation under a setting or a row on the Settings
 * screens ("Holding new orders before batching", "Label bought when Print is
 * clicked"): small text with an optional glyph on its first line.
 *
 * The quiet sibling of `WarningNotice`: no box, no fill. `tone="warning"` turns
 * the words amber for a row whose setting carries a risk, and `tone="error"`
 * turns them destructive red for a value that blocks a save. The glyph should
 * change with the tone, so colour is never the only signal (DESIGN.md
 * section 16).
 *
 * The words are the caller's, already translated. `id` lands on the words, not
 * the row, so a control's `aria-describedby` reads exactly the sentence.
 *
 * The glyph sits on the first line (`items-start` plus a 2px nudge), so a note
 * that wraps keeps its glyph beside the first line instead of drifting to the
 * middle of the block.
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type NoteLineTone = 'muted' | 'warning' | 'error';

const TEXT_TONE: Record<NoteLineTone, string> = {
  muted: 'text-muted-foreground',
  warning: 'text-amber-800',
  error: 'text-destructive',
};

const ICON_TONE: Record<NoteLineTone, string | undefined> = {
  // The muted glyph takes the words' colour.
  muted: undefined,
  warning: 'text-amber-700',
  error: 'text-destructive',
};

export interface NoteLineProps {
  /** The note, already translated. */
  children: ReactNode;
  /** An optional glyph on the first line; decorative, the words carry the meaning. */
  icon?: LucideIcon;
  tone?: NoteLineTone;
  /** `p` when the note sits among paragraphs; `div` (the default) inside a row. */
  as?: 'p' | 'div';
  /** On the words, for a control's `aria-describedby`. */
  id?: string;
  /** On the root (spacing, measure). */
  className?: string;
}

export function NoteLine({ children, icon: Icon, tone = 'muted', as: Root = 'div', id, className }: NoteLineProps) {
  return (
    <Root className={cn('flex items-start gap-1 text-xs', TEXT_TONE[tone], className)}>
      {Icon && <Icon className={cn('mt-0.5 h-3 w-3 shrink-0', ICON_TONE[tone])} aria-hidden="true" />}
      <span id={id}>{children}</span>
    </Root>
  );
}
